import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { mensajeDeError } from '../lib/mensajeDeError';
import GraficaDePeso from '../components/GraficaDePeso';
import {
    FileText, TrendingUp,
    Activity, ChevronRight, ChevronLeft, Calendar
} from 'lucide-react';
import InformeMensual from '../components/reports/InformeMensual';
import EvolucionMedidas from '../components/EvolucionMedidas';
import ComparativaCliente from '../components/ComparativaCliente';
import PuntosDeControl from '../components/puntos/PuntosDeControl';
import Comparador from '../components/puntos/Comparador';
import Diario from '../components/Diario';
// «Tus días», la lista del punto 19, montada con sus datos. Vive en `CheckInsPage` porque
// allí están sus ayudantes (la línea del día, el detalle y los huecos) y desde el 16-09 se
// pinta aquí: un solo sitio para el Diario y el historial (punto 23).
import { TusDias } from './CheckInsPage';
import TresFotos from '../components/reports/TresFotos';
import TusFotosYMetricas from '../components/TusFotosYMetricas';
import CampoDePeso from '../components/CampoDePeso';
import FormularioReporte from '../components/reports/FormularioReporte';
import { verComo } from '../lib/modoRevision';
import { plazoEnPalabras, hoyLocal } from '../lib/horaEspana';
import { kg } from '../lib/pesoValido';

const ORANGE = '#FF671F';

/**
 * EL HISTORIAL, POR CICLOS Y BLOQUES (punto 40 del repaso del 8-09).
 *
 * Jesús: «Su subtítulo dice "los que mandaste y los informes que te di", y luego son 16
 * fechas sueltas de 2023 a 2026, todas iguales, todas con Ver mi informe del mes y solo el
 * peso.» Y lo que tiene que ser: «Por ciclos y bloques, y distinguiendo lo que mandó él de
 * lo que le devolviste tú.»
 *
 * La lista llega del más reciente al más antiguo, así que los de un mismo ciclo vienen
 * seguidos y basta con cortar cuando cambia: así el orden de la lista no se toca y el
 * «Cargar más» sigue cayendo donde tiene que caer.
 *
 * El ciclo lo pone el servidor (`core.puntos.situar_reportes`, el mismo criterio que «Tus
 * puntos de control»). Los reportes anteriores al cuaderno de ciclos no tienen ciclo
 * apuntado: llegan como un tramo aproximado, llamado por sus meses («Marzo a junio de
 * 2024») y sin número, y el encabezado dice que es aproximado.
 */
const porCiclos = (lista) => {
    const grupos = [];
    for (const r of lista) {
        const clave = r.ciclo_clave || r.ciclo_id || 'sin-ciclo';
        const ultimo = grupos[grupos.length - 1];
        if (!ultimo || ultimo.clave !== clave) {
            grupos.push({
                clave,
                // Si ni siquiera se pudo fechar el reporte, el servidor no manda etiqueta.
                // Se dice, y sigue saliendo en la lista: nunca un hueco.
                titulo: r.ciclo_etiqueta || 'Sin fecha de ciclo',
                aproximado: !!r.ciclo_aproximado,
                reportes: [r],
            });
        } else {
            ultimo.reportes.push(r);
        }
    }
    return grupos;
};

// «3 que mandaste · 2 con respuesta», que es la mitad que faltaba de lo que pedía: separar
// lo suyo de lo que se le devolvió. Sin respuestas no se dice nada: un «0 con respuesta»
// debajo de cada ciclo sería recordárselo en cada línea.
const cuentaDelCiclo = (reportes) => {
    const conRespuesta = reportes.filter(r => r.trainer_feedback).length;
    const suyos = `${reportes.length} que mandaste`;
    return conRespuesta ? `${suyos} · ${conRespuesta} con respuesta` : suyos;
};

// «22 de agosto», y con el año solo si no es el de hoy: un ajuste del año pasado sin año
// parece de este.
const _fechaDelAjuste = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T12:00:00');
    if (Number.isNaN(d.getTime())) return '';
    const conAnio = d.getFullYear() !== new Date().getFullYear();
    return d.toLocaleDateString('es-ES', {
        day: 'numeric', month: 'long', ...(conAnio ? { year: 'numeric' } : {}),
    });
};
// Un bloque de macros en palabras: «180 g de proteína, 250 g de hidratos, 60 g de grasa».
// Los nombres son los que devuelve `/macros/historial` (proteina/hidratos/grasa).
const _macrosEnPalabras = (bloque) => {
    if (!bloque) return '';
    return [['proteina', 'proteína'], ['hidratos', 'hidratos'], ['grasa', 'grasa']]
        .filter(([k]) => bloque[k] != null)
        .map(([k, nombre]) => `${bloque[k]} g de ${nombre}`)
        .join(', ');
};

/**
 * EL ÚLTIMO AJUSTE DE MACROS DENTRO DE EVOLUCIÓN, para el cliente con entrenador (doc 19-08,
 * bloque 09). Hasta el 2-09 aquí se pintaba la tabla ENTERA de ajustes, la misma de Mis
 * macros. Jesús, en el doc del 2-09: «En Mis macros está el mismo histórico entero, así que
 * en Evolución se queda solo el último ajuste y un enlace. La tabla vive en un sitio, no en
 * dos». Se queda la fecha, sus macros de entreno y descanso, y el enlace a Mis macros (que
 * para él abre en lectura, con el histórico entero).
 *
 * Se pide lo mismo que antes (`/macros/historial`) y se usa solo la primera entrada, que es
 * la vigente: el servidor ya las manda de la más nueva a la más vieja y sin las de fecha
 * futura.
 */
const UltimoAjuste = ({ api }) => {
    const navigate = useNavigate();
    const [entradas, setEntradas] = useState(null);
    useEffect(() => {
        let vivo = true;
        api.get('/macros/historial')
            .then(r => { if (vivo) setEntradas(r.data?.entradas || []); })
            .catch(() => { if (vivo) setEntradas([]); });
        return () => { vivo = false; };
    }, [api]);
    // El mismo corte que tenía la tabla: con una sola entrada no hay «ajuste», hay los
    // macros de partida, y ya los ve en Mis macros y en su calculadora.
    if (!entradas || entradas.length < 2) return null;
    const ultimo = entradas[0];
    const entreno = _macrosEnPalabras(ultimo.entreno);
    const descanso = _macrosEnPalabras(ultimo.descanso);
    const peri = _macrosEnPalabras(ultimo.peri);
    return (
        <div className="bg-card border border-border rounded-2xl p-4 space-y-2" data-testid="ultimo-ajuste">
            <p className="caption">Tu último ajuste</p>
            <p className="text-sm font-bold text-foreground">{_fechaDelAjuste(ultimo.fecha)}</p>
            <div className="text-sm text-muted-foreground space-y-0.5">
                {entreno && <p><span className="text-foreground font-medium">Entreno:</span> {entreno}</p>}
                {peri && <p><span className="text-foreground font-medium">Perientreno:</span> {peri}</p>}
                {descanso && <p><span className="text-foreground font-medium">Descanso:</span> {descanso}</p>}
            </div>
            <button type="button" data-testid="ver-mis-macros"
                onClick={() => navigate('/dashboard/macro-calculator')}
                className="text-sm font-bold text-brand inline-flex items-center gap-1 hover:underline">
                {/* SIN UNA TERCERA CUENTA DE AJUSTES (Francisco, 11-09: «tres cuentas
                    distintas de tus ajustes en dos pantallas: "12 últimos ajustes",
                    "cuatro últimos ajustes" y "los 147 ajustes"»).

                    Las tres eran verdad por separado -- la gráfica pinta doce, la tabla
                    cuatro y en total hay ciento cuarenta y siete -- y juntas no hay quien
                    las case: el enlace prometía 147 y al otro lado se encontraba 4. Aquí
                    se quita el número, que es el que sobra: este botón dice A DÓNDE va, no
                    cuántas filas hay. Las otras dos cuentas viven en Mis macros
                    (`GraficaDeAjustes` y `HistorialDeMacros`) y no se tocan desde aquí. */}
                Ver mis macros y todo mi histórico <ChevronRight className="w-4 h-4" />
            </button>
        </div>
    );
};

// Estado de la ventana de envío. Cada cadencia tiene la suya (el semanal, viernes 10:00
// -> sábado 10:00, doc 21-08); las fechas las manda el servidor y aquí solo se enseñan,
// en la hora del navegador (_plazoDelCliente).
const WindowBanner = ({ w }) => {
    const base = "rounded-2xl p-4 border text-sm flex items-center gap-2";
    if (w?.revision) {
        return (
            <div className={`${base} border-brand/40 bg-brand/5 text-foreground`}>
                <Calendar className="w-4 h-4 text-brand flex-shrink-0" />
                Modo revisión: estás viendo el {w.tipo_label?.toLowerCase()} fuera de su semana. Enviarlo no funcionará.
            </div>
        );
    }
    if (!w || !w.due) {
        return (
            <div className={`${base} border-border bg-muted text-foreground/60`}>
                <Calendar className="w-4 h-4 text-foreground/40 flex-shrink-0" />
                Esta semana no toca reporte. Te avisaremos cuando abra la ventana.
            </div>
        );
    }
    if (w.submitted) {
        return (
            <div className={`${base} border-green-500/40 bg-green-500/5 text-foreground`}>
                <Calendar className="w-4 h-4 text-green-500 flex-shrink-0" />
                Ya enviaste tu {w.tipo_label?.toLowerCase()} de esta semana. ¡Bien hecho!
            </div>
        );
    }
    // EL REPORTE COMPRADO NO TIENE PLAZO (pantalla 08 del 7-09): no es una cita de su
    // calendario, es lo que pagó, y no puede caducársele. El cartel de siempre le decía
    // «rellena tu tu reporte antes del .» -- con el «tu» duplicado y sin fecha, porque no
    // hay ninguna --, así que este caso lleva su propia frase, que además es la promesa.
    if (w.comprado) {
        return (
            <div className={`${base} border-brand/40 bg-brand/5 text-foreground`}>
                <Calendar className="w-4 h-4 text-brand flex-shrink-0" />
                {w.promesa || 'Rellénalo cuando quieras.'}
            </div>
        );
    }
    if (w.is_open) {
        return (
            <div className={`${base} border-yellow-500/40 bg-yellow-500/5 text-foreground`}>
                <Calendar className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                Ventana abierta: rellena tu {w.tipo_label?.toLowerCase()} antes del {_plazoDelCliente(w.closes_at) || w.closes_label}.
            </div>
        );
    }
    const before = w.opens_at && new Date(w.opens_at).getTime() > Date.now();
    // Cerrada del todo: la fecha que sirve es la del SIGUIENTE, no la de la ventana que
    // ya pasó. El servidor la manda en `proximo` (con su ISO para poder decir la hora).
    const proximaApertura = _plazoDelCliente(w.proximo?.abre) || w.proximo?.abre_label;
    return (
        <div className={`${base} ${before ? 'border-yellow-500/40 bg-yellow-500/5' : 'border-red-500/40 bg-red-500/5'} text-foreground`}>
            <Calendar className={`w-4 h-4 flex-shrink-0 ${before ? 'text-yellow-500' : 'text-red-500'}`} />
            {/* «La semana que viene» no es una fecha, y el servidor sabe cuándo vuelve a abrir:
                lo mandaba en `opens_label` y aquí no se usaba (Jesús, 11-08). */}
            {/* UN BOTÓN GRIS INFORMA; UNO QUE NO ESTÁ, NO (doc 21-08): cerrada o aún sin
                abrir, se dice CUÁNDO se enciende y CON HORA -- «Se abre el viernes a las
                10:00» --, en la hora del navegador del cliente. */}
            {before
                ? `Todavía no toca. Se abre el ${_plazoDelCliente(w.opens_at) || w.opens_label}.`
                : proximaApertura
                    ? `La ventana de esta semana se cerró. Se abre el ${proximaApertura}.`
                    : w.opens_label
                        ? `La ventana de esta semana se cerró. Se abría el ${w.opens_label}.`
                        : 'La ventana de esta semana se cerró. Espera a la semana que viene.'}
        </div>
    );
};

/**
 * LA PORTADA DE SEGUIMIENTO (documento del 10-08, pantalla 20).
 *
 * «Una sola entrada. Lo que toca ahora, arriba y en naranja; lo que ya hizo, en gris.
 * Nunca tiene que elegir entre cuatro formularios.»
 *
 * Sustituye a las dos entradas de hoy -- Reportes y Check-ins eran dos sitios distintos en
 * el menú -- y a que esta pantalla abriera con el formulario del mes desplegado entero.
 *
 * Lo que el documento pide y AQUÍ TODAVÍA NO ESTÁ, para que no se dé por hecho: la tarjeta
 * de «Esta semana» con el balance («cuadraste 5 de 7 días») necesita un dato que la app no
 * calcula hoy, y la revisión mensual en seis pasos (pantallas 22 a 24) es otra pantalla.
 * Aquí cada tarjeta lleva a lo que ya existe.
 */
const TarjetaSeguimiento = ({ cuando, titulo, sub, extra, cta, onClick, tono = 'gris', testid }) => {
    const destacada = tono === 'ahora';
    return (
        <button onClick={onClick} data-testid={testid}
            className={`w-full text-left rounded-2xl p-4 border transition-colors ${
                destacada ? 'border-brand/50 bg-brand/5 hover:bg-brand/10' : 'border-border bg-card hover:border-foreground/20'}`}>
            {cuando && <p className={`text-xs font-bold uppercase tracking-wider ${destacada ? 'text-brand' : 'text-muted-foreground'}`}>{cuando}</p>}
            <p className={`text-lg font-bold text-foreground ${cuando ? 'mt-1' : ''}`}>{titulo}</p>
            {sub && <p className="text-[15px] text-muted-foreground mt-0.5">{sub}</p>}
            {extra && <p className="text-[13px] text-muted-foreground mt-1">{extra}</p>}
            {cta && (
                <span className={`inline-flex items-center gap-1 mt-3 text-sm font-bold ${destacada ? 'text-brand' : 'text-foreground/70'}`}>
                    {cta} <ChevronRight className="w-4 h-4" />
                </span>
            )}
        </button>
    );
};

// Una de las puertas de Seguimiento: el cierre del día, Reportes, Evolución y Diario. Sin
// etiqueta de "cuándo" porque están siempre: el título, lo que hay dentro y la flecha.
const PuertaSeguimiento = ({ titulo, sub, onClick, testid }) => (
    <button onClick={onClick} data-testid={testid}
        className="w-full text-left rounded-2xl p-4 border border-border bg-card hover:border-foreground/20 transition-colors flex items-center gap-3">
        <span className="flex-1 min-w-0">
            <span className="block text-lg font-bold text-foreground">{titulo}</span>
            <span className="block text-[15px] text-muted-foreground">{sub}</span>
        </span>
        <ChevronRight className="w-5 h-5 text-foreground/30 flex-shrink-0" />
    </button>
);

// EL PLAZO, EN LA HORA DEL CLIENTE (doc 21-08, apartado 15). El plazo se FIJA en España
// -- el servidor manda `closes_at` en UTC y la regla es de allí --, pero se ENSEÑA en la
// hora del navegador: al que vive en Canarias o está de viaje, "hasta el sábado a las
// 10:00" a secas le miente una hora o varias. Si el reloj del navegador coincide con el
// de España se enseña tal cual y sin coletilla; si no, la hora local con la de España
// entre paréntesis. Se monta por partes porque el formato de un tirón mete comas donde
// el doc no las tiene. Este es el punto central: WindowBanner, la portada y el
// formulario (plazoLabel) formatean por aquí.
const MADRID = 'Europe/Madrid';

const _partesPlazo = (d, tz) => {
    const partes = new Intl.DateTimeFormat('es-ES', {
        ...(tz ? { timeZone: tz } : {}), weekday: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d);
    const p = (tipo) => partes.find(x => x.type === tipo)?.value || '';
    return {
        texto: `${p('weekday')} ${p('day')} a las ${p('hour')}:${p('minute')}`,
        hora: `${p('hour')}:${p('minute')}`,
    };
};

const _plazoDelCliente = (iso) => {
    const d = iso ? new Date(iso) : null;
    if (!d || isNaN(d)) return null;
    const espana = _partesPlazo(d, MADRID);
    let local = espana;
    try { local = _partesPlazo(d); } catch { /* sin huso legible: se queda la de España */ }
    // Mismo reloj (España, o un huso con su misma hora): tal cual y sin coletilla.
    if (local.texto === espana.texto) return espana.texto;
    return `${local.texto} (${espana.hora} h España)`;
};

const _diaMadrid = (d) => new Intl.DateTimeFormat('en-CA', {
    timeZone: MADRID, year: 'numeric', month: '2-digit', day: '2-digit',
}).format(d);

// Aquí vivía `_cuantoQueda` («te quedan 2 días»), que iba pegado al plazo de la tarjeta
// «Pendiente». La maqueta del 1-09 deja esa tarjeta con el título y el botón y nada más, y
// el plazo pasa a la línea de Inicio, que es donde el cliente lo lee de verdad y donde
// ahora se dice con palabras: «Tienes hasta mañana jueves a las ocho».

const PortadaSeguimiento = ({ windowState, vencido, conDiario, conCierre, puedeMandarReporte,
                             tieneReportes, onRevision, onEvolucion, onHistorial, onDiario,
                             onCierre }) => {
    // Le toca reporte y no lo ha mandado: es lo único que va arriba y en naranja.
    // LA FECHA LÍMITE VA EN LA TARJETA: la ventana son cuatro días y sin fecha nadie sabe
    // cuánto margen tiene («hasta el jueves 15», Jesús 11-08). El servidor ya mandaba
    // `closes_label` y `opens_label`; aquí no se usaban.
    //
    // «TE TOCA» ES CUANDO YA SE PUEDE, NO CUANDO ESTÁ PREVISTO.
    // La tarjeta miraba solo `due`, que dice que este periodo lleva reporte, no que la
    // ventana esté abierta. Resultado: salía en naranja con «Empezar» y detrás aparecía el
    // formulario en gris con «se abre el viernes». Prometía algo que no se podía hacer, que
    // es de lo que se queja Jesús el 11-08 sobre esta pantalla. Con `is_open` la tarjeta
    // dice lo que hay: cuando abra, en naranja; hasta entonces, en gris y con la fecha.
    //
    // TRES COSAS Y NADA MÁS (doc 16-08, T6): Reportes, Evolución y Diario. Los informes van
    // DENTRO de Reportes, no como una cuarta cosa, y la tarjeta «¿Cómo vas hoy?» se cayó de
    // aquí: al cierre del día se entraba solo desde Inicio.
    //
    // ESA ÚLTIMA PARTE YA NO VALE: el doc «El día · parte 1» (30-08) dice «se llega por dos
    // sitios, esa línea y la pestaña Seguimiento», y es más reciente. La puerta del cierre
    // vuelve, con las otras. Lo que sí sigue en pie del 16-08 es lo demás: los informes
    // dentro de Reportes y ninguna segunda pantalla de Seguimiento.
    //
    // PASADA LA HORA, EL REPORTE DESAPARECE. Un reporte vencido en la portada es una puerta
    // que no lleva a ningún sitio: el servidor lo rechaza fuera de plazo. Se cae de aquí
    // igual que de Inicio, y en su lugar queda lo que sí puede hacer: lo siguiente.
    // SEGUIMIENTO EN MODO LECTURA (decisión de Jesús del 24-08). El que no vende ningún
    // reporte entra aquí a ver lo suyo -- su historial, su Evolución y su Diario -- pero
    // no tiene reporte que mandar: ni la tarjeta del formulario ni el calendario del
    // «lo próximo es...», que sería anunciarle una cita que su plan no incluye.
    const proximo = puedeMandarReporte ? windowState?.proximo : null;
    const yaPuede = windowState?.is_open !== false;
    const yaMandado = !!windowState?.submitted;
    const tocaRevision = puedeMandarReporte && !!windowState?.due && !yaMandado && yaPuede && !vencido;
    // Le toca este periodo pero la ventana todavía no ha abierto.
    const aunNoAbre = puedeMandarReporte && !!windowState?.due && !yaMandado && !yaPuede && !vencido;
    const hayReporteALaVista = puedeMandarReporte && (tocaRevision || yaMandado || aunNoAbre);
    // El plazo, en hora de España y con lo que queda: la ventana son unos días y sin fecha
    // nadie sabe cuánto margen tiene (Jesús, 11-08).
    const plazo = _plazoDelCliente(windowState?.closes_at);
    // Aún sin abrir: cuándo se enciende, con hora y en su huso (doc 21-08).
    const apertura = _plazoDelCliente(windowState?.opens_at) || windowState?.opens_label;

    return (
        <div className="space-y-3" data-testid="portada-seguimiento">
            {/* LA TARJETA, COMO LA MAQUETA DEL 1-09 («La tarjeta y los avisos»). Tres
                cosas cambian, y las tres las dice él:

                - FUERA EL «ESTA SEMANA»: «en algo quincenal miente la semana que no
                  toca». Queda la palabra sola: Pendiente / Hecho.
                - PENDIENTE, SIN NADA DEBAJO: ni la frase de las preguntas ni el plazo.
                  «Lo único que tiene que ver es que le toca.» El plazo se lo da la línea
                  de Inicio, que es donde lo va a leer.
                - HECHO, CON UNA HORA: «Respondiste a tiempo y ahora nos toca a nosotros.
                  Te decimos algo antes del viernes a las tres de la tarde, hora de
                  España.» Decía «Ya lo mandaste. Lo estamos mirando», «que no compromete
                  a nada»; ahora se compromete, y la frase viene del servidor
                  (`core/promesa_del_reporte`) porque el día prometido es el mismo que
                  vigila el aviso del equipo y no puede haber dos.

                El «aún no abre» no sale en el documento: se queda como estaba, gris y
                diciendo cuándo se enciende, que es lo del doc 21-08. */}
            {hayReporteALaVista && (
                <TarjetaSeguimiento
                    testid="seg-revision"
                    cuando={tocaRevision ? 'Pendiente' : yaMandado ? 'Hecho' : 'Aún no'}
                    titulo={windowState?.tipo_label || 'Tu revisión'}
                    sub={yaMandado
                        ? (windowState?.promesa
                            || 'Respondiste a tiempo y ahora nos toca a nosotros.')
                        : aunNoAbre
                            ? 'Unas breves preguntas para ajustar tus macros si hace falta.'
                            : null}
                    extra={aunNoAbre && apertura ? `Se abre el ${apertura}.` : null}
                    /* EL «VER» QUE NO LLEVABA A NINGUNA PARTE (2-09). Los tres estados de la
                       tarjeta compartían botón y destino: decía «Ver» y llamaba a `onRevision`,
                       o sea abría el formulario. Con la ventana cerrada ahí no hay nada que
                       abrir, así que el botón se pulsaba y no pasaba nada.
                       Ahora cada estado lleva lo suyo: pendiente, «Empezar» al formulario; ya
                       mandado, «Ver» al historial, que es donde está lo que mandó; y si aún no
                       abre, NINGÚN botón, porque todavía no hay nada que ver y la propia
                       tarjeta ya dice cuándo se enciende. */
                    cta={tocaRevision ? 'Empezar' : yaMandado ? 'Ver' : null}
                    tono={tocaRevision ? 'ahora' : 'gris'}
                    onClick={tocaRevision ? onRevision : onHistorial} />
            )}

            {/* SOLO EN EL CLON DE PRUEBAS. Allí la ventana se abre a la fuerza para poder
                probar el reporte cualquier día, y sin decirlo se presta a confusión: uno
                pensaría que un cliente puede rellenarlo hoy. Así que se dice, y con las
                fechas de verdad al lado. En producción este aviso no existe, porque el
                servidor solo manda `abierta_por_pruebas` si la variable está encendida. */}
            {windowState?.abierta_por_pruebas && (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2"
                    data-testid="ventana-abierta-por-pruebas">
                    <p className="text-[13px] font-semibold text-amber-500">
                        Abierta solo para pruebas
                    </p>
                    <p className="text-[13px] text-muted-foreground">
                        De verdad {apertura ? `se abre el ${apertura}` : 'abre otro día'}
                        {plazo ? ` y se cierra el ${plazo}` : ''}. Hoy la puedes rellenar
                        igualmente porque estás en el clon de pruebas.
                    </p>
                </div>
            )}

            {/* Y detrás del que toca, el siguiente. Va pegada a su tarjeta porque habla de
                ella. Con esto el cliente ve su calendario y no solo el día de hoy: el del
                Nivel 2 sabe que después de este quincenal le llega el mensual, que es el que
                pide fotos y medidas y con el que hay que contar antes. */}
            {proximo && hayReporteALaVista && (
                <p className="text-[13px] text-muted-foreground px-1 -mt-1" data-testid="seg-proximo">
                    Después de este: tu {proximo.tipo_label.toLowerCase()}, el {proximo.abre_label}.
                </p>
            )}

            {/* Sin reporte a la vista -- porque no toca o porque se pasó la hora -- lo que
                queda es su calendario, para que la pantalla no calle sobre lo que viene. */}
            {!hayReporteALaVista && proximo && (
                <p className="text-[13px] text-muted-foreground px-1" data-testid="seg-proximo">
                    Lo próximo es tu {proximo.tipo_label.toLowerCase()}, el {proximo.abre_label}.
                </p>
            )}

            {/* LA SEGUNDA PUERTA DEL CIERRE DEL DÍA (doc «El día · parte 1», 30-08): «Se
                llega por DOS SITIOS, esa línea y la pestaña Seguimiento.»

                Y no estaba. El doc del 16-08 había quitado de aquí la tarjeta «¿Cómo vas
                hoy?» -- «al cierre del día se entra desde Inicio» -- y a la vez se quitó
                «Check-ins» del menú, así que se quedó con UNA sola puerta: la fila de
                Inicio. Y esa fila no está siempre: de 15:00 a 17:00 no hay ningún día
                abierto y desaparece, y en cuanto cierra el día se queda con su marca de
                hecho. Entre medias, el cliente que quería mirar lo que contó ayer no tenía
                por dónde entrar. Manda lo más reciente, que es el doc del 30-08.

                Va la primera de las puertas porque es la diaria: las otras tres son
                archivo. Y no sale si apagó «Rellenar el cierre del día», que es lo que ese
                interruptor significa -- «no podrás registrar tus datos del día» --: una
                puerta a un formulario que él mismo apagó prometería lo contrario. */}
            {conCierre && (
                <PuertaSeguimiento
                    testid="seg-cierre"
                    titulo="El cierre del día"
                    sub="Cómo te fue cada día"
                    onClick={onCierre} />
            )}

            {/* LA PUERTA DE REPORTES, SOLO SI HAY REPORTES (Jesús, punto 8 del 9-09):
                «Edu Lara, Reto 60 días, un plan que NO lleva reportes. Nunca ha mandado
                ninguno ni va a recibir ninguno. Esa puerta lleva a una lista vacía para
                siempre».

                Y NO SE QUITA A TODO EL QUE NO LOS VENDE, que es lo que parecería. Su
                decisión del 24-08 sigue en pie: «el que no vende ningún reporte entra aquí
                a ver LO SUYO», y eso es el cliente antiguo que mandó reportes y cuyo plan
                ya no los incluye. A ése la puerta le tiene que seguir saliendo. La regla
                que cumple las dos: se enseña si su plan los vende O si tiene alguno.

                Es la misma que ya lleva el Diario aquí abajo, con sus mismas palabras: una
                puerta que lleva a una lista vacía para siempre es peor que no estar. */}
            {(puedeMandarReporte || tieneReportes) && (
                <PuertaSeguimiento
                    testid="seg-historial"
                    titulo="Reportes"
                    sub="Los que mandaste y los informes que te di"
                    onClick={onHistorial} />
            )}

            <PuertaSeguimiento
                testid="seg-evolucion"
                titulo="Evolución"
                sub="Tus fotos y tus métricas"
                onClick={onEvolucion} />

            {/* El Diario solo se ofrece si su interruptor está encendido: una puerta que
                lleva a un «todavía no está disponible» es peor que no estar. */}
            {conDiario && (
                <PuertaSeguimiento
                    testid="seg-diario"
                    titulo="Diario"
                    sub="Tus notas, día a día"
                    onClick={onDiario} />
            )}
        </div>
    );
};

// Modo revisión (solo equipo): `?ver=mensual`, `?ver=quincenal` o `?ver=semanal` abren el
// formulario aunque no sea su semana ni su fin de semana. Es la única forma de repasar los
// textos del reporte un martes, o de ver el mensual (con las diez medidas) el mes que toca
// quincenal. Enviarlo sigue pasando por el servidor, que no sabe nada de esto y lo rechaza
// fuera de plazo: se puede MIRAR, no colar.
const VENTANA_DE_MENTIRA = (tipo) => ({
    due: true, is_open: true, submitted: false, revision: true,
    tipo_label: { mensual: 'Reporte mensual', quincenal: 'Reporte quincenal',
                  semanal: 'Reporte semanal' }[tipo],
    tipos: [tipo],
    opens_label: 'modo revisión', closes_label: 'modo revisión',
});

/**
 * LA SALIDA DEL QUE SE LE PASÓ EL REPORTE (Francisco, 8-09).
 *
 * Su aviso promete dos cosas -- «dinos si lo mandas esta semana o te lo aplazamos» -- y las
 * dos son lo mismo por dentro: reabrirle la ventana. Así que aquí hay un solo botón, y se
 * dice lo que pasa al pulsarlo, que es lo que el aviso no decía.
 */
const SeTePasoElReporte = ({ api, onHecho }) => {
    const [yendo, setYendo] = useState(false);
    const [hecho, setHecho] = useState(false);

    const reabrir = async () => {
        setYendo(true);
        try {
            const r = await api.post('/reports/aplazar', {});
            setHecho(true);
            toast.success(r.data?.titulo || 'Te lo hemos reabierto', {
                description: r.data?.mensaje, duration: 8000,
            });
            if (onHecho) onHecho();
        } catch (e) {
            toast.error(mensajeDeError(e, 'No hemos podido reabrírtelo. Inténtalo en un momento.'));
        } finally { setYendo(false); }
    };

    if (hecho) return null;
    return (
        <div className="surface p-4 space-y-3" data-testid="se-te-paso-el-reporte">
            <p className="font-bold text-foreground">No nos llegó tu reporte</p>
            <p className="text-sm text-muted-foreground">
                Sin él no podemos ajustarte. Si lo mandas esta semana, te lo reabrimos ahora
                mismo y sigues como si nada.
            </p>
            <button type="button" onClick={reabrir} disabled={yendo}
                data-testid="reabrir-el-reporte"
                className="w-full rounded-xl py-3 text-sm font-bold text-white uppercase tracking-wider disabled:opacity-40"
                style={{ backgroundColor: '#FF671F' }}>
                {yendo ? 'Un momento...' : 'Lo mando esta semana'}
            </button>
        </div>
    );
};

const ReportsPage = () => {
    const { api, token, profile, user, pantalla, can } = useAuth();
    // La Evolución completa (medidas y comparativa) va detrás de su interruptor del panel
    // (T6). Con él apagado se queda la gráfica de peso, que es lo que hay hoy en producción.
    const evolucionCompleta = pantalla('t6_evolucion');
    const diarioActivo = pantalla('t5_diario');
    const revision = verComo(user);
    const tipoRevision = ['mensual', 'quincenal', 'semanal'].includes(revision) ? revision : null;
    // QUIÉN PUEDE MANDAR UN REPORTE. La pantalla la abre ahora la llave del cierre del día
    // (CAP.CIERRE_DIA, decisión del 24-08), que la lleva todo el mundo: sin esto, los 81
    // clientes cuyo plan no vende reportes entrarían al formulario de uno. El envío no
    // depende solo de esto -- el servidor lo rechaza igual --, pero enseñar un formulario
    // que va a rebotar es prometer algo que no hay.
    // El modo revisión del equipo sigue entrando: es para repasar los textos.
    // Y EL QUE HA COMPRADO SU REPORTE, aunque su plan no lleve ninguno (pantalla 08 del
    // 7-09). El que paga su ajuste personalizado se autogestiona: `can('reportes')` es false
    // para él, así que esta pantalla no le enseñaba el reporte que acababa de pagar. Lo dice
    // el servidor, que es quien sabe si lo compró, y por eso `windowState` se declara aquí
    // arriba: se leía cincuenta líneas antes de existir.
    const [windowState, setWindowState] = useState(null);   // ventana de envío (la de su cadencia)
    const puedeMandarReporte = can('reportes') || !!tipoRevision || !!windowState?.comprado;
    const [reports, setReports] = useState([]);
    const [evolution, setEvolution] = useState(null);
    const [loading, setLoading] = useState(true);
    // SI QUIERE EL CIERRE DEL DÍA, para la puerta de la portada (doc «El día», 30-08). Lo
    // dice el mismo sitio que la fila de Inicio (`/checkins/estado` -> `quiere_cierre`), no
    // un segundo cálculo: si mañana cambia la regla, cambia en uno.
    const [quiereCierre, setQuiereCierre] = useState(null);
    // LA PANTALLA ABRE EN LA PORTADA, TAMBIÉN EN EL ORDENADOR.
    //
    // `seccionAbierta` dice en cuál de las cuatro tarjetas ha entrado, y mientras vale null
    // se ve la portada (documento del 10-08, pantalla 20).
    //
    // Esto era solo del teléfono, porque el encargo era no tocar el escritorio, y en el
    // ordenador seguían las tres pestañas con el formulario entero desplegado de entrada:
    // peso, vídeo, las diez medidas, dos preguntas, tres barras, notas, objetivo, dos
    // preguntas más, enviar y las tres fotos, tocara reporte o no. Y en gris, con un «la
    // ventana se cerró» encima: se le enseñaba todo lo que no podía hacer (Jesús, 11-08).
    // Es el mismo cliente y el mismo trámite, así que es la misma solución.

    // En modo revisión se entra directamente al formulario: es lo que se viene a mirar.
    // Y «?abrir=evolucion» aterriza en Evolución: es a donde apuntan el botón «Ver mi
    // evolución» de Mis macros y la ruta vieja de macros para los planes con entrenador
    // (doc 19-08, bloque 09).
    // Y «?aplazar=1» aterriza en el formulario con el aplazamiento abierto: es el camino
    // desde «No puedo esta semana» del aviso (doc 19-08, bloque 11).
    // Y «?abrir=diario» aterriza en el Diario (24-08): el cierre del día enlaza aquí con
    // «Ver mi diario», y sin esto el enlace dejaba al cliente en la portada a buscarlo.
    // Y «?abrir=peso» aterriza en Evolución con el campo del peso ya enfocado: es el camino
    // desde la fila «Hoy toca pesarte» de Inicio («Todo lo validado antes del 1 de
    // septiembre», bloque 4: «al tocarla le abre el campo en Seguimiento -- no le manda a
    // buscarlo»).
    // Y «?abrir=comparar» aterriza en el comparador de puntos (fase 3 del doc del 2-09,
    // 4-09): es a donde apuntan «Comparar con cualquier punto ›» de la gráfica de peso y
    // «Comparar este punto con otro ›» del detalle de un punto (este con `&punto=ID`, el
    // punto que ya va elegido).
    const abrirPedida = new URLSearchParams(window.location.search).get('abrir');
    const aplazarPedido = new URLSearchParams(window.location.search).get('aplazar') === '1';
    const pesarseHoy = abrirPedida === 'peso';
    const [seccionAbierta, setSeccionAbierta] = useState(
        tipoRevision || aplazarPedido ? 'form'
            : abrirPedida === 'evolucion' || abrirPedida === 'peso' ? 'evolution'
                : abrirPedida === 'diario' ? 'diario'
                    : abrirPedida === 'comparar' ? 'comparar'
                        // Y «?abrir=fotos» aterriza en las tres fotos (Trello 21-09, César): es a
                        // donde manda Inicio cuando el cliente dejó las del cuestionario para luego.
                        : abrirPedida === 'fotos' ? 'fotos' : null);
    // LOS DOS ENLACES AL COMPARADOR SALEN DE DENTRO DE ESTA MISMA PANTALLA (la gráfica y el
    // detalle de un punto viven en Evolución), así que navegan a la misma ruta con otra
    // consulta y la página no se vuelve a montar: el estado inicial de arriba no se
    // reevalúa. Aquí se escucha la consulta y se cambia de sección cuando cambia.
    const location = useLocation();
    const navigate = useNavigate();
    const puntoPedido = new URLSearchParams(location.search).get('punto');
    useEffect(() => {
        const abrir = new URLSearchParams(location.search).get('abrir');
        if (abrir === 'comparar') setSeccionAbierta('comparar');
        else if (abrir === 'evolucion') setSeccionAbierta('evolution');
        else if (abrir === 'fotos') setSeccionAbierta('fotos');
    }, [location.search]);
    // EL FORMULARIO NO SE ABRE SIN LLAVE (24-08): ni por «?aplazar=1» ni por un enlace
    // viejo. Al que solo tiene Seguimiento en lectura se le deja en la portada.
    const vista = seccionAbierta === 'form' && !puedeMandarReporte ? null : seccionAbierta;
    // Si el plazo ya pasó. Lo dice el servidor en `items[].overdue` y no se calcula aquí:
    // la hora buena es la suya, no la del reloj del teléfono. Pasada la hora, el reporte
    // pendiente desaparece de esta pantalla (y de Inicio, que ya lo hacía).
    const [vencido, setVencido] = useState(false);
    const [prev, setPrev] = useState(null);                 // último reporte (referencia de medidas)

    // El informe del mes: se pide solo cuando abre uno, porque cruza dietas, check-ins
    // y macros de todo el periodo y no tiene sentido calcularlo para la lista entera.
    const [informeAbierto, setInformeAbierto] = useState(null);   // id del reporte abierto
    const [informe, setInforme] = useState(null);
    const [cargandoInforme, setCargandoInforme] = useState(false);

    const verInforme = async (reportId) => {
        if (informeAbierto === reportId) { setInformeAbierto(null); setInforme(null); return; }
        // Y SE VA A DONDE SE PINTA (Francisco, 14-09: «el botón de ver mi informe no hace
        // nada»). El informe se dibuja DENTRO de la lista del historial, y el «Ver mi
        // informe» del paso 4 se pulsa desde la pantalla de después de enviar: abría el
        // informe en una pestaña que no estabas mirando, así que desde fuera el botón
        // parecía muerto. No lo estaba, estaba trabajando donde no se veía.
        setSeccionAbierta('history');
        setInformeAbierto(reportId);
        setInforme(null);
        setCargandoInforme(true);
        try {
            const r = await api.get(`/reports/${reportId}/informe`);
            setInforme(r.data);
            // Y se lleva la vista hasta él: en el historial de alguien con veinte reportes,
            // el que acaba de mandar puede quedar fuera de la pantalla y el botón seguiría
            // pareciendo que no hace nada. Se espera al pintado, que si no el sitio al que
            // saltar todavía no existe.
            setTimeout(() => {
                document.querySelector(`[data-testid="ver-informe-${reportId}"]`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 150);
        } catch (e) {
            toast.error('No hemos podido crear tu informe');
            setInformeAbierto(null);
        } finally {
            setCargandoInforme(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch solo al montar
    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            // La confirmación de huecos ya no se pide aquí: cada reporte trae los suyos
            // dentro (`/reports/formulario`), con los días de rutina que le faltan por
            // registrar en vez de una cuenta de check-ins.
            const [reportsRes, evolutionRes, dueRes, prevRes, cierreRes] = await Promise.all([
                api.get('/reports'),
                api.get('/reports/evolution'),
                api.get('/reports/due').catch(() => ({ data: { window: null } })),
                api.get('/reports/previous').catch(() => ({ data: null })),
                // El día del CLIENTE y su hora, igual que los pide Inicio: el día vivido es
                // el del reloj del NAVEGADOR (España manda en los plazos, no en qué día es
                // hoy para él). Aquí solo hace falta el interruptor, pero se pide igual que
                // en los otros dos sitios para no tener tres formas de preguntar lo mismo.
                api.get(`/checkins/estado?fecha=${hoyLocal()}`
                    + `&hora_cliente=${new Date().getHours()}`).catch(() => ({ data: null })),
            ]);
            setReports(reportsRes.data);
            setHasMore(reportsRes.data.length === 50);
            setEvolution(evolutionRes.data);
            setWindowState(tipoRevision ? VENTANA_DE_MENTIRA(tipoRevision) : (dueRes.data?.window || null));
            setVencido(!tipoRevision && (dueRes.data?.items || []).some(i => i.overdue));
            setPrev(prevRes.data && Object.keys(prevRes.data).length ? prevRes.data : null);
            // Sin respuesta la puerta sale igual (null se trata como «sí»): apagarlo es lo
            // raro, y esconder la puerta por un fallo de red dejaría al cliente con la
            // única que ya tenía, que es justo lo que se está arreglando.
            setQuiereCierre(cierreRes.data ? cierreRes.data.quiere_cierre !== false : null);
        } catch (error) {
            console.error('Error fetching reports:', error);
        } finally {
            setLoading(false);
        }
    };

    // Paginación del historial: "Cargar más" trae el siguiente bloque
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const loadMore = async () => {
        setLoadingMore(true);
        try {
            const res = await api.get('/reports', { params: { skip: reports.length } });
            setReports(prev => [...prev, ...res.data]);
            setHasMore(res.data.length === 50);
        } catch { /* silencioso */ }
        finally { setLoadingMore(false); }
    };

    // Qué reporte toca esta semana. Lo usa la subida de fotos suelta: las fotos son del
    // mensual, pero se pueden subir cualquier día (punto 21 / 2.2).
    const esMensual = (windowState?.tipos || []).includes('mensual');

    // EL ENVÍO VIVE EN EL FORMULARIO (T7, T8 y T9). Aquí estaba el `handleSubmit` de los
    // dos reportes juntos: peso, medidas, tres deslizadores y tres preguntas, todo en el
    // mismo sitio. Ahora cada reporte tiene su cuestionario, el mensual pasa por la
    // pantalla de revisión y lo que se le dice al terminar depende de su plan, así que el
    // envío se fue con ellos a `components/reports/FormularioReporte`.
    const formOpen = windowState ? (windowState.is_open && !windowState.submitted) : true;

    // Sin formatear: la gráfica necesita la fecha de verdad para poner el eje en el tiempo.
    // Antes se le daba ya convertida a «9 ago», y con dietas desde 2022 hay cuatro «9 ago»
    // distintos que el eje juntaba en una sola columna.
    // CON SU MARCA (12-09): el servidor dice de qué pesajes se fía (`fiable`, tope de 3 kg
    // por semana) y aquí se perdía en el camino, así que la gráfica pintaba igual el 118
    // entre setentas. Se pasa tal cual: el que decide qué hacer con él es quien dibuja.
    const weightData = evolution?.weight?.map(
        w => ({ fecha: w.date, peso: w.value, fiable: w.fiable })) || [];
    // El último pesaje conocido, para la línea de abajo del campo («Último registro: 72,7 kg
    // · 24 de agosto») y para medir contra él el salto que canta. Sale de la MISMA serie que
    // la curva: si se pidiera aparte volverían a poder decir cosas distintas.
    const ultimoPesaje = weightData.length
        ? { valor: weightData[weightData.length - 1].peso, fecha: weightData[weightData.length - 1].fecha }
        : null;
    const pesoDiasAtras = evolution?.peso_dias_atras;

    if (loading) {
        return (
            <div className="px-4 pt-6 pb-28">
                <div className="animate-pulse space-y-4">
                    <div className="h-8 bg-muted rounded w-1/3" />
                    <div className="h-48 bg-card rounded-2xl" />
                </div>
            </div>
        );
    }

    return (
        <div className="px-4 pt-6 pb-28 max-w-2xl mx-auto space-y-4">
            {/* Header */}
            <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${ORANGE}20` }}>
                    <FileText className="w-5 h-5" style={{ color: ORANGE }} />
                </div>
                <div>
                    {/* Un solo nombre: en el menú pone «Seguimiento» y aquí ponía «Mis
                        reportes» en el ordenador, así que la misma sección se llamaba de
                        dos maneras según el aparato desde el que la abrieras. */}
                    <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Barlow Condensed', letterSpacing: '0.05em' }} data-testid="reports-heading">
                        SEGUIMIENTO
                    </h1>
                    {/* Lo que hay dentro, con sus nombres. Eran tres -- reportes, evolución
                        y diario --, y desde el doc «El día · parte 1» (30-08) el cierre del
                        día vuelve a tener aquí su puerta, así que se nombra: un rótulo que
                        enumera lo de dentro y se deja una fuera es peor que no enumerar. */}
                    <p className="text-xs text-foreground/30">Tu día, tus reportes, tu evolución y tu diario</p>
                </div>
            </div>

            {/* LA PORTADA DE SEGUIMIENTO (documento del 10-08, pantalla 20). Una sola
                entrada en vez de dos -- Reportes y Check-ins eran dos sitios distintos --,
                con lo que toca ahora arriba y lo que ya está hecho debajo en gris: «nunca
                tiene que elegir entre cuatro formularios».

                Antes esta pantalla abría directamente con el formulario del reporte entero
                desplegado: peso, las diez medidas, los huecos, las notas, tres preguntas,
                las fotos y enviar. 3.770 px, cuatro pantallas y media de móvil, tanto si le
                tocaba reporte como si no. */}
            {/* «NO NOS LLEGÓ TU REPORTE», CON SU SALIDA (Francisco, 8-09).
                Ese aviso le dice «dinos si lo mandas esta semana o te lo aplazamos», y hasta
                hoy no podía hacer ninguna de las dos: al pulsarlo llegaba aquí, donde su
                reporte ya no está, y el botón de aplazar vivía dentro de un formulario que
                con la ventana cerrada no se pinta.

                VA ARRIBA Y FUERA DE LAS VISTAS, no dentro del formulario: el cliente entra
                por el enlace del aviso y cae en la portada, así que si la salida vive dentro
                de una pantalla a la que hay que entrar, es como no tenerla.

                Mientras su aviso siga vivo -- siete días desde el cierre, que es lo que dura
                -- se le ofrece reabrirlo. Y solo entonces: al que ya puede mandarlo, o ya lo
                mandó, esta tarjeta le sobra. */}
            {windowState?.se_le_paso && !formOpen && !windowState?.submitted && (
                <SeTePasoElReporte api={api} onHecho={fetchData} />
            )}

            {!vista && (
                <PortadaSeguimiento
                    windowState={windowState}
                    vencido={vencido}
                    conDiario={diarioActivo}
                    /* La misma llave que abre esta pantalla y la fila de Inicio
                       (CAP.CIERRE_DIA, 24-08), más su interruptor de Mi perfil. */
                    conCierre={can('cierre_dia') && quiereCierre !== false}
                    puedeMandarReporte={puedeMandarReporte}
                    tieneReportes={reports.length > 0}
                    onRevision={() => setSeccionAbierta('form')}
                    onEvolucion={() => setSeccionAbierta('evolution')}
                    onHistorial={() => setSeccionAbierta('history')}
                    onDiario={() => setSeccionAbierta('diario')}
                    /* El cierre del día es una PANTALLA aparte, no una sección de ésta:
                       se navega a ella, y desde allí la vuelta ya existe («‹ Seguimiento»,
                       en CheckInsPage), que hasta hoy era una vuelta a un sitio del que no
                       se podía venir. */
                    onCierre={() => navigate('/dashboard/checkins')}
                />
            )}

            {/* Al entrar en una sección, la portada deja sitio y aparece la vuelta. El
                comparador cuelga de Evolución (se entra desde ahí), así que su vuelta es a
                Evolución y no a la portada; va por la URL para que la sección y la
                consulta digan lo mismo si recarga. */}
            {vista && vista !== 'comparar' && (
                <button onClick={() => setSeccionAbierta(null)} data-testid="volver-seguimiento"
                    className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground">
                    <ChevronLeft className="w-4 h-4" /> Seguimiento
                </button>
            )}
            {vista === 'comparar' && (
                <button onClick={() => navigate('/dashboard/reports?abrir=evolucion', { replace: true })} data-testid="volver-evolucion"
                    className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground">
                    <ChevronLeft className="w-4 h-4" /> Evolución
                </button>
            )}

            {/* Aquí iban las tres pestañas del escritorio. Ya no hacen falta: la portada
                hace lo mismo diciendo además cuál toca ahora, y tener las dos cosas a la
                vez era ofrecer dos navegaciones para la misma pantalla. */}

            {/* ── EL FORMULARIO (T7 y T8) ──
                Ya no es un formulario con campos sueltos: son dos cuestionarios distintos
                -- el quincenal de cuatro preguntas y el mensual por bloques -- y el
                mensual además cambia según lo que incluya su plan. Todo eso vive en
                `components/reports/FormularioReporte`; aquí solo queda la ventana de
                envío, que es de esta pantalla. */}
            {vista === 'fotos' && (
                <div className="space-y-4" data-testid="seccion-fotos">
                    <TresFotos api={api} token={token} esMensual={false} />
                </div>
            )}
            {vista === 'form' && (
                <div className="space-y-4">
                    <WindowBanner w={windowState} />
                    {windowState && !windowState.due ? null : formOpen ? (
                        <FormularioReporte
                            api={api} token={token}
                            tipoRevision={tipoRevision}
                            windowState={{
                                ...windowState,
                                // La semana que decidió ESTE reporte (doc 19-08): la de su
                                // rutina si la tiene; sin rutina, la de su ciclo de siempre.
                                semana: windowState?.semana_reporte ?? profile?.week,
                                // «hasta mañana jueves a las ocho», que es como lo escribe
                                // la cabecera del 1-09. Antes iba «Hasta el jueves 4 a las
                                // 20:00 · te queda 1 día»: la misma información contada
                                // como un albarán.
                                plazoLabel: plazoEnPalabras(windowState?.closes_at)?.hasta,
                                // Y la fecha sola, sin el «hasta»: el aviso del paso 3 la
                                // mete dentro de una frase suya («tienes como fecha límite
                                // el lunes 14 a las seis») y con la otra le salía «el hasta
                                // el lunes 14».
                                cierreLabel: plazoEnPalabras(windowState?.closes_at)?.cuando,
                            }}
                            prev={prev}
                            perfilCliente={profile}
                            onEnviado={fetchData}
                            // «Ver mi informe ›» del paso 4: abre el informe con el mismo
                            // camino que el historial, no con una pantalla aparte.
                            onVerInforme={verInforme} />
                    ) : (
                        // Ventana cerrada o ya mandado: el formulario no se pinta en gris
                        // detrás de un cartel (punto 4.18). Lo que sí sigue estando son las
                        // fotos: se suben solas, en su propia petición, y que se pidan en el
                        // mensual es una cosa y que el resto del mes no se puedan subir es otra.
                        <TresFotos api={api} token={token} esMensual={esMensual} />
                    )}
                </div>
            )}

            {/* ── EVOLUCIÓN ──
                «Tus fotos y tus métricas». Hasta el doc 16-08 aquí solo estaba la gráfica de
                peso: ni fotos, ni medidas, ni comparativa, cuando todo eso ya existía en la
                ficha del panel. Ahora se enseña lo mismo que ve su entrenador, con los dos
                cambios de T6: dos fotos por defecto y sin el % graso, que lo pone Jesús. */}
            {vista === 'evolution' && (
                <div className="space-y-4">
                    <div>
                        <p className="text-lg font-bold text-foreground">Evolución</p>
                        <p className="text-[15px] text-muted-foreground">Tus fotos y tus métricas</p>
                    </div>

                    {/* LOS TRES BOTONES (doc 19-08, «Lo que falta en Seguimiento»): subir
                        fotos, añadir medidas y actualizar el % de grasa, cuando quiera. La
                        pantalla decía «se piden en el reporte mensual» y no dejaba hacer
                        nada.
                        Y CON EL PLAZO CUANDO LO HAY (doc del 2-09): la ventana va por
                        `windowState`, que es el mismo dato con el que la tarjeta de arriba
                        dice hasta cuándo. */}
                    <TusFotosYMetricas api={api} token={token} profile={profile}
                        ventana={windowState} onGuardado={fetchData} />


                    {/* EL CAMPO DEL PESO, AQUÍ («Todo lo validado antes del 1 de
                        septiembre», bloque 4): «abierto todo el año», «es el único sitio
                        donde el peso se escribe». Se escribía dentro del cierre del día,
                        con lo que el que no cierra el día -- la mayoría -- no tenía dónde.

                        Y VA EN LA MISMA TARJETA QUE LA CURVA, no en una aparte: las dos se
                        llaman «Tu peso», y dos tarjetas seguidas con el mismo título es
                        exactamente lo que hace que nadie lea ninguna. Aquí escribe el
                        pesaje y justo debajo ve lo que ese pesaje hace con su línea. La
                        tarjeta ya no depende de que haya datos: el que todavía no tiene
                        curva es el que más necesita el campo. */}
                    <div className="bg-card border border-border rounded-2xl p-4 space-y-4">
                        <CampoDePeso api={api} diasAtras={pesoDiasAtras}
                            ultimo={ultimoPesaje} abrir={pesarseHoy}
                            pesoDelDia={evolution?.peso_del_dia ?? null}
                            pesoSemanal={evolution?.peso_semanal ?? null}
                            detalleSemanal={evolution?.peso_semanal_detalle ?? null}
                            onGuardado={fetchData} />
                        {weightData.length > 0 ? (
                            <div className="pt-4 border-t border-border">
                                {/* La gráfica es la MISMA que ve su entrenador (punto 4.13). Aquí
                                    los ejes iban pintados de blanco a pelo, y esta tarjeta es
                                    blanca en tema claro: eran fechas y kilos escritos en blanco
                                    sobre blanco. */}
                                {/* Con su objetivo al lado: el color del cambio se decide
                                    contra él (doc 23-08, bloque 10). Desde el 4-09 (fase 2
                                    del doc del 2-09) la gráfica recibe la FICHA ENTERA, no
                                    solo el `goal`: pinta encima la tarjeta del objetivo del
                                    ciclo, el actual, el foco y por dónde va (`objetivo_actual`,
                                    `foco`, `ciclo_actual`), todo de solo lectura porque lo
                                    pone el entrenador. */}
                                {/* EN DOS BLOQUES (doc de Jesús del 2-09): arriba «Este ciclo»,
                                    con la curva solo desde `cycle_start` y los kilos de estas
                                    N semanas (`cycle_total_weeks`); debajo «Desde que entraste»,
                                    en texto y sin la gráfica larga. Los rótulos los pone la
                                    propia gráfica, por eso ya no va el título «Tu curva» aquí:
                                    salían dos títulos seguidos para lo mismo. */}
                                {/* EL CICLO VIGENTE ES EL DEL CUADERNO, NO EL ANCLA (5-09). Aquí
                                    iba `cycle_start`, que es el ancla del cobro y se queda
                                    quieta: a Jesús la gráfica le decía «este ciclo» desde el
                                    1 de enero mientras el cuaderno decía que su ciclo abierto
                                    empezó el 18 de junio. Le pasa a cualquiera cuyo plan haya
                                    dado la vuelta. Manda `ciclos.inicio`, que es lo que la
                                    propia gráfica usa cuando no se le pasa nada; el ancla se
                                    queda de reserva para el que aún no tiene cuaderno. */}
                                <GraficaDePeso puntos={weightData} perfil={profile}
                                    porSemanas={evolution?.curva_semanal || null}
                                    desdeElCiclo={profile?.ciclo_actual?.inicio || profile?.cycle_start}
                                    semanasDelCiclo={profile?.ciclo_actual?.semanas || profile?.cycle_total_weeks}
                                    conComparador={evolucionCompleta} />
                            </div>
                        ) : (
                            <div className="pt-4 border-t border-border text-center">
                                <TrendingUp className="w-10 h-10 text-foreground/20 mx-auto mb-3" />
                                <p className="text-foreground font-bold mb-1">Todavía no hay curva</p>
                                <p className="text-xs text-foreground/30">
                                    Con dos pesajes ya se ve por dónde vas.
                                </p>
                            </div>
                        )}
                    </div>

                    {evolucionCompleta && (
                        <>
                            {/* TUS PUNTOS DE CONTROL (doc de Jesús del 2-09; fase 3, 4-09):
                                uno por reporte, del más antiguo al de hoy, debajo del peso y
                                antes de la comparativa. Al tocar uno se abre su detalle en el
                                mismo sitio, y desde ahí se va al comparador. */}
                            <PuntosDeControl api={api} />
                            {/* EL MISMO «INICIO DEL CICLO» QUE LA GRÁFICA DE ARRIBA. Aquí se
                                pasaba `faseDesde`, una prop que `ComparativaCliente` dejó de
                                aceptar cuando llegó el cuaderno de ciclos (fase 1), así que
                                `desdeElCiclo` entraba vacío y la foto caía en `cycle_start`
                                mientras la gráfica ya leía el cuaderno. Y no son lo mismo:
                                cuando el ancla se queda vieja, `inicio_del_ciclo_vigente`
                                cuenta las vueltas que hayan pasado -- en producción, 127 de
                                los 149 clientes con ancla tienen el ciclo abierto en otra
                                fecha --, así que la misma pantalla llamaba «este ciclo» a dos
                                días distintos según se mirara la curva o la foto. */}
                            <ComparativaCliente api={api} reports={reports}
                                desdeElCiclo={profile?.ciclo_actual?.inicio || profile?.cycle_start} />
                            {/* La misma tabla que la ficha del panel, con el tono de la app del
                                cliente: la diferencia con la toma anterior y la columna Total.
                                Con el perfil, para que entren también las medidas sueltas
                                («Añadir medidas · cuando quieras») y las del alta: sin él, el
                                que las apuntaba por ahí seguía leyendo aquí que no tenía
                                ninguna. */}
                            <EvolucionMedidas reports={reports} perfil={profile} />
                        </>
                    )}

                    {/* SU ÚLTIMO AJUSTE DE MACROS, AQUÍ, para el que se los lleva un
                        entrenador (doc 19-08, bloque 09): su histórico va «al lado de su
                        peso, sus medidas y sus fotos. Que es donde se ve que estás
                        trabajando». Desde el doc del 2-09 ya no es la tabla entera, que
                        vive en Mis macros: solo el último ajuste y el enlace. */}
                    {profile?.macros_ajustables?.puede === false && (
                        <UltimoAjuste api={api} />
                    )}
                </div>
            )}

            {/* ── EL COMPARADOR DE PUNTOS (fase 3 del doc del 2-09, 4-09) ──
                Dos puntos, nunca tres: se elige el otro (el de hoy ya va elegido, o el que
                llega en `?punto=`), se ven las dos fotos con peso, medidas, grasa y macros,
                y se genera la imagen si el cliente quiere. Va detrás del mismo interruptor
                que el resto de la Evolución completa. */}
            {vista === 'comparar' && (
                evolucionCompleta ? (
                    <Comparador api={api} puntoInicial={puntoPedido} />
                ) : (
                    <div className="bg-card border border-border rounded-2xl p-8 text-center">
                        <p className="text-sm text-muted-foreground">El comparador todavía no está disponible.</p>
                    </div>
                )
            )}

            {/* ── EL DIARIO (T5) ──
                Vive DENTRO de Seguimiento, no es una pestaña nueva del menú. Solo lectura:
                aquí cae lo que ya escribió en el cierre del día y en sus entrenos.

                Y AQUÍ VIVE TAMBIÉN «TUS DÍAS» (punto 23 del doc del 24-08, cerrado el
                16-09): «el Diario y el historial, un solo sitio». La lista de días estaba en
                la pantalla del cierre y el Diario aquí, así que el cliente tenía lo mismo
                repartido en dos sitios que no se nombraban.

                EL ORDEN NO ES CASUAL: primero la lista, que es para repasar de un vistazo
                -- «bajando el dedo se ve la semana entera: dónde flojeó, qué días no cerró y
                si el peso baja» --, y debajo el Diario, que es para leer. Son sus dos
                frases del encabezado del bloque: «una lista para repasar y un detalle para
                leer». */}
            {vista === 'diario' && (
                diarioActivo ? (
                    <div className="space-y-5">
                        <TusDias />
                        <Diario api={api} />
                    </div>
                ) : (
                    <div className="bg-card border border-border rounded-2xl p-8 text-center">
                        <p className="text-sm text-muted-foreground">Tu diario todavía no está disponible.</p>
                    </div>
                )
            )}

            {/* ── HISTORY TAB ── */}
            {vista === 'history' && (
                <div className="space-y-3">
                    {reports.length > 0 ? porCiclos(reports).map((grupo) => (
                      <div key={grupo.clave} className="space-y-3 pt-4 first:pt-0">
                        {/* EL ENCABEZADO DEL CICLO. Es lo único que hacía falta para que las
                            fechas dejaran de ser «todas iguales»: saber de qué ciclo es cada
                            tramo de la lista. Y si el ciclo no está en el cuaderno se dice que
                            es aproximado, en vez de dar un número como si fuera exacto.

                            El aire de separación va arriba, en el grupo, y no aquí: dentro de
                            su grupo el encabezado siempre es el primero, así que un `first:`
                            en él no separaría nada y los ciclos se pegarían unos con otros. */}
                        <div data-testid={`ciclo-${grupo.clave}`}>
                            <p className="caption">
                                {grupo.titulo}
                                {grupo.aproximado && (
                                    <span className="font-normal normal-case text-muted-foreground"> · aproximado</span>
                                )}
                            </p>
                            <p className="text-xs text-muted-foreground">{cuentaDelCiclo(grupo.reportes)}</p>
                        </div>
                        {grupo.reportes.map((report) => {
                        // SIN INFORME DEL MES EN SU PLAN (catálogo del 29-09: el Reto12en12
                        // no lo lleva). Su reporte sale igual, pero sin el «Ver mi informe
                        // del mes», que el servidor ya no le monta. Los que se le guardaron
                        // antes (traen `informe_estado`) se le siguen ofreciendo.
                        // Sin `tipo` cuenta como del mes: es el que pasa el equipo.
                        const sinInforme = (!report.tipo || report.tipo === 'mensual') && !can('informe_mensual')
                            && !report.informe_estado;
                        return (
                        <div key={report.id} className="bg-card border border-border rounded-2xl p-4">
                            <button onClick={() => { if (!sinInforme) verInforme(report.id); }} data-testid={`ver-informe-${report.id}`}
                                disabled={sinInforme}
                                className="w-full text-left flex items-start justify-between mb-3 disabled:cursor-default">
                                <div>
                                    <p className="text-xs text-foreground/40 uppercase tracking-wider">
                                        {new Date(report.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                                        {/* Y el bloque, que es la otra mitad de «por ciclos y
                                            bloques»: dentro de un ciclo, lo que sitúa un reporte
                                            es el bloque que cierra, no el día del mes. */}
                                        {report.bloque != null && (
                                            <span className="text-foreground/30"> · Bloque {report.bloque}</span>
                                        )}
                                    </p>
                                    <p className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Barlow Condensed' }}>
                                        {/* Con coma y a la décima, como toda la app (punto 71): salía «74.1 kg». */}
                                        {kg(report.weight)} <span className="text-base text-foreground/40">kg</span>
                                    </p>
                                    {!sinInforme && <p className="text-[11px] mt-0.5" style={{ color: ORANGE }}>
                                        {/* NO TODOS SON «DEL MES» (Francisco, 11-09: «el
                                            informe de un reporte quincenal se llama
                                            "informe mensual"»). El enlace del historial
                                            decía «del mes» en las dieciséis filas, fueran
                                            del reporte que fueran. El tipo viaja en el
                                            reporte desde el 4-09; sin él, «mi informe». */}
                                        {informeAbierto === report.id
                                            ? 'Ocultar mi informe'
                                            : report.tipo === 'quincenal' ? 'Ver mi informe quincenal'
                                            : report.tipo === 'semanal' ? 'Ver mi informe semanal'
                                            : report.tipo === 'mensual' ? 'Ver mi informe del mes'
                                            : 'Ver mi informe'}
                                    </p>}
                                </div>
                                {!sinInforme && <ChevronRight className={`w-4 h-4 text-foreground/20 mt-1 transition-transform ${informeAbierto === report.id ? 'rotate-90' : ''}`} />}
                            </button>

                            {informeAbierto === report.id && (
                                <div className="mb-3">
                                    {cargandoInforme
                                        ? <p className="text-sm text-foreground/40 py-4 text-center">Creando tu informe...</p>
                                        // EL INFORME NO SALE HASTA QUE LO REVISAN (T9). Mientras está
                                        // pendiente no se le enseña el "te falta una foto" del informe
                                        // sin generar: ahí no le falta nada a él, le falta a Jesús.
                                        : informe?.motivo === 'pendiente_revision'
                                            ? <p className="text-sm text-muted-foreground py-4 text-center" data-testid="informe-pendiente">
                                                {informe.mensaje}
                                              </p>
                                            : <InformeMensual informe={informe} api={api} token={token}
                                                onPedirFotos={() => setSeccionAbierta('form')} />}
                                </div>
                            )}
                            {/* Con el informe abierto, estos dos sobran: el cumplimiento de
                                verdad (el de lo registrado) y la explicación del coach ya van
                                dentro, y verlos dos veces confunde sobre cuál es el bueno.
                                Los deslizadores además son justo lo que la especificación
                                manda sustituir por la confirmación de huecos. */}
                            {informeAbierto !== report.id && (report.training_compliance != null || report.nutrition_compliance != null) && (
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="bg-muted rounded-xl px-3 py-2 flex items-center gap-2">
                                        <Activity className="w-3.5 h-3.5" style={{ color: ORANGE }} />
                                        <span className="text-xs text-foreground/60">Entreno <span className="text-foreground font-bold">{report.training_compliance != null ? `${report.training_compliance}%` : '-'}</span></span>
                                    </div>
                                    <div className="bg-muted rounded-xl px-3 py-2 flex items-center gap-2">
                                        <Activity className="w-3.5 h-3.5 text-green-400" />
                                        <span className="text-xs text-foreground/60">Nutrición <span className="text-foreground font-bold">{report.nutrition_compliance != null ? `${report.nutrition_compliance}%` : '-'}</span></span>
                                    </div>
                                </div>
                            )}
                            {informeAbierto !== report.id && report.trainer_feedback && (
                                <div className="mt-3 p-3 rounded-xl border" style={{ backgroundColor: `${ORANGE}10`, borderColor: `${ORANGE}30` }}>
                                    <p className="text-xs font-bold mb-1" style={{ color: ORANGE }}>Feedback del entrenador</p>
                                    <p className="text-sm text-foreground/70">{report.trainer_feedback}</p>
                                </div>
                            )}
                        </div>
                        );})}
                      </div>
                    )) : (
                        <div className="bg-card border border-border rounded-2xl p-8 text-center">
                            <Calendar className="w-10 h-10 text-foreground/20 mx-auto mb-3" />
                            <p className="text-foreground/40 text-sm">No hay reportes anteriores.</p>
                        </div>
                    )}
                    {hasMore && (
                        <button onClick={loadMore} disabled={loadingMore} data-testid="reports-load-more"
                            className="w-full py-3 rounded-2xl border border-border text-sm text-foreground/60 hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50">
                            {loadingMore ? 'Cargando...' : 'Cargar más reportes'}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ReportsPage;
